/*
It takes a string type S as input and checks if it matches the pattern of a snake_case string (i.e., a string with an underscore followed by another character).
If the string matches the pattern, it transforms the string by capitalizing the character after the underscore and removing the underscore.
If the string does not match the pattern, it returns the original string unchanged.
*/
type TSnakeToCamel<S extends string> = S extends `${infer P}_${infer R}`
  ? `${P}${Capitalize<TSnakeToCamel<R>>}`
  : S;

/*
It takes a generic type T as input and returns a transformed version of the type.
If the type is an array, it recursively transforms the elements of the array.
If the type is an object, it transforms the keys of the object.
*/
type TTransformToCamelCase<T> = T extends (infer U)[]
  ? TTransformToCamelCase<U>[]
  : T extends Record<string, unknown>
    ? {
        [K in keyof T as K extends string ? TSnakeToCamel<K> : never]: TTransformToCamelCase<T[K]>;
      }
    : T;

/**
 * Takes a string in snake_case and transforms it to camelCase.
 * @example
 * snakeToCamel('hello_world') // 'helloWorld'
 * @param {string} str - The string to transform
 * @returns {string} - The transformed string
 */
const snakeToCamel = (str: string): string =>
  str.replace(/_([a-zA-Z0-9])/g, (_, char: string) => char.toUpperCase());

/**
 * Iteratively transforms the keys of an object from snake_case to camelCase.
 * @example
 * const data = {
 *   user_id: 123,
 *   user_name: 'John Doe',
 *   user_address: {
 *     street_address: '123 Main St',
 *     city: 'New York',
 *     state: 'NY',
 *   },
 * };
 * transformKeysToCamelCase(data)
 * data = {
 *   userId: 123,
 *   userName: 'John Doe',
 *   userAddress: {
 *     streetAddress: '123 Main St',
 *     city: 'New York',
 *     state: 'NY',
 *   },
 * };
 * @param {Record<string, unknown> | unknown[]} data - The data to transform
 * @returns {TTransformToCamelCase<T>} - The transformed data
 */
export const transformKeysToCamelCase = <T>(data: T): TTransformToCamelCase<T> => {
  if (!data || typeof data !== 'object') return data as TTransformToCamelCase<T>;

  const stack: Array<{
    target: Record<string, unknown> | unknown[];
    value: Record<string, unknown> | unknown[];
  }> = [];

  const result: Record<string, unknown> | unknown[] = Array.isArray(data) ? [] : {};

  if (Array.isArray(data) || (typeof data === 'object' && data !== null)) {
    stack.push({ target: result, value: data as Record<string, unknown> | unknown[] });
  }

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;

    const { target, value } = current;

    if (Array.isArray(value)) {
      // If the value is an array, create a new array and push it to the target
      for (let i = 0; i < value.length; i++) {
        if (Array.isArray(value[i]) || (value[i] && typeof value[i] === 'object')) {
          const newTarget = Array.isArray(value[i]) ? [] : {};
          (target as unknown[])[i] = newTarget;
          stack.push({ target: newTarget, value: value[i] as Record<string, unknown> | unknown[] });
        } else {
          (target as unknown[])[i] = value[i];
        }
      }
    } else if (value && typeof value === 'object') {
      // If the value is an object, create a new object and push it to the target
      for (const [key, val] of Object.entries(value)) {
        const newKey = snakeToCamel(key);
        if (Array.isArray(val) || (val && typeof val === 'object')) {
          const newTarget = Array.isArray(val) ? [] : {};
          (target as Record<string, unknown>)[newKey] = newTarget;
          stack.push({ target: newTarget, value: val as Record<string, unknown> | unknown[] });
        } else {
          (target as Record<string, unknown>)[newKey] = val;
        }
      }
    }
  }

  return result as TTransformToCamelCase<T>;
};
